/**
 * 인포그래픽과 자막이 겹치기 시작하는 정규화 y 경계 — **remotion 렌더 호스트**의 자막 상단이다.
 *
 * 이 선을 쓰는 표면 = 실렌더가 remotion(`renderMediaOnWeb`)인 경로(에이전트 스튜디오 director·
 * `SceneInfographicsSection`). 호스트는 화면비가 정한다:
 *   · 9:16 → `StoryTemplate` 자막 블록 상단 = 세이프존으로 접힌 값 0.628675(2026-09-23 · 종전 500/640 = 0.78125 는
 *     쇼츠 버튼·설명글 자리였다 · 해상도·자막크기 무관 상수, #2036 ①).
 *   · 16:9 → `StoryTemplate` 이 `Overlay169Template` 로 위임(#2036 ④). 자막은 captionCenterY pill 바이고
 *     상단 정규화 y 는 스킨·body.size 에 따라 다르다. **전 스킨(141종) 실측**:
 *       - 기본 body 40 → 최빡 0.8102(briefing/blueprint 3줄·econstory 0.8115)
 *       - 편집 상한 body 45(CONSERVATIVE_MAX_BODY_SIZE_169) → 최빡 0.8008
 *       - seniorlife 팩토리 body 64(투명 자막바 시니어 롱폼) → **0.7976 = 전 조합 최빡**
 *     0.78 은 그 최빡(0.7976)보다도 **보수적으로 아래**라 어떤 스킨·크기에서도 자막바 상단을 넘지
 *     않는다(경고가 조금 이를 뿐 겹침을 놓치지 않는다). 기하 SSOT = overlay169-caption-geometry.ts
 *     (`captionBarTop`/`captionBarBottom`). anti-drift = `tests/unit/issue-1884-caption-size-ef.test.ts`
 *     의 "Overlay169 자막바 경계" 표가 `0.78 ≤ 실측 최빡`을 못 박는다 — 더 빡빡한 스킨이 들어오면 거기서
 *     먼저 red 가 난다. ★값을 고치면 그 표부터 다시 재라(EF 는 배포 게이트).
 *   알 수 없는 화면비는 더 오래 검증된 9:16 경계로 안전하게 폴백한다.
 *
 * ⚠️ **캔버스 번인 썰씨네 경로는 이 선이 아니다**: `mediabunnyRender.drawSsulcineCaption`(화면비 무관
 *   하단앵커 블록)을 타는 바로제작 표면(`InfographicsPanel`, `ssulcineCaption:true`)은 아래
 *   `ssulcineCaptionSafeTop`(16:9 md=0.6111)을 쓴다. 두 선은 16:9 에서 갈리는데, 그것은 값 드리프트가
 *   아니라 **실렌더 호스트가 다르기 때문**이다(remotion Overlay169 pill vs 캔버스 번인 썰씨네 블록).
 *   director 요청은 `ssulcineCaption` 플래그로 어느 호스트인지 EF 에 알리고, EF 는 그에 맞는 선을 고른다
 *   (`infographic-director-normalize.ts`).
 */
export function infographicCaptionSafeTop(aspect: unknown): number {
  // 📐 9:16 = 세이프존으로 **접힌** 시네마틱 자막 상단(2026-09-23 전역 적용 2단계). 종전 500/640 은 쇼츠
  //   버튼·설명글 자리(78~88%)였다. 프론트 `foldCineStoryCaptionBody`(페이지 2줄 · 줄 간격 33)와 같은 셈이다.
  //   16:9 = Overlay169 자막바가 세이프존으로 접힌 뒤(바닥 ≤ 82.87%) 전 스킨 최빡 윗변 0.63032(브리핑·설계도 3줄 @45)
  //   보다 보수적으로 아래인 0.63(2026-09-23 · 종전 0.78 은 접기 전 최빡 0.7976 기준이었다). 표 = 1884 테스트.
  if (aspect === "16:9") return 0.63;
  return Math.min(500, 640 * (1 - SAFE_CORE_BOTTOM["9:16"]) - 2 * 33) / 640;
}

/**
 * 📐 세이프존 core 하단 여백(프레임 높이 대비) — 프론트 정본 `src/lib/safe-zone.ts` `SAFE_ZONE[a].core.bottom` 의
 * **사본**이다(Edge Function 은 src 를 import 하지 않는다). 값이 갈리면 `tests/unit/issue-1884-caption-size-ef.test.ts`
 * 가 먼저 red 가 된다(사본 == 정본 대조). ★정본을 고치면 여기도 고쳐라★ — EF 는 배포 게이트다.
 */
export const SAFE_CORE_BOTTOM: Readonly<Record<InfographicAspect, number>> = { "9:16": 0.2682, "16:9": 0.1713 };

/** 📐 세이프존 core 상단 여백 — 위와 같은 **사본**(정본 `SAFE_ZONE[a].core.top`). 인포그래픽 윗변의 하한이다. */
export const SAFE_CORE_TOP: Readonly<Record<InfographicAspect, number>> = { "9:16": 0.2109, "16:9": 0.1273 };

/** 📐 세이프존 core 좌·우 여백 — 위와 같은 **사본**(정본 `SAFE_ZONE[a].core.left/right`). 세로판 오른쪽이 넓은 것은 버튼 열(14.75%). */
export const SAFE_CORE_LEFT: Readonly<Record<InfographicAspect, number>> = { "9:16": 0.0553, "16:9": 0.0417 };
export const SAFE_CORE_RIGHT: Readonly<Record<InfographicAspect, number>> = { "9:16": 0.1475, "16:9": 0.0417 };

/** 인포그래픽 **중심** x 가 core 가장자리에서 떨어져야 하는 몫 — 종전 전역 범위 0.08~0.92 의 «가장자리 8%» 를 core 기준으로 옮긴 값. */
const DIRECTOR_X_EDGE_MARGIN = 0.08;

/**
 * 📐 AI 가 고른 인포그래픽 중심 x 의 허용 범위(세이프존 3-b · 2026-09-26) — 종전엔 화면비와 무관하게 0.08~0.92 였고,
 * 세로판에서는 0.92 가 오른쪽 버튼 열(85.25%~) 한가운데라 AI 가 오른쪽에 둔 배지·가격표가 버튼에 가렸다.
 * 이제 core 좌·우 가장자리에서 같은 8% 를 뗀다: 9:16 ≈ 0.1353~0.7725 · 16:9 ≈ 0.1217~0.8783.
 * ★새로 만드는 인포그래픽에만 닿는다★ — 이 값은 서버가 AI 출력을 정규화할 때 한 번 쓰고, 저장된 x 는 다시 읽지 않는다.
 */
export function directorInfographicXRange(aspect: InfographicAspect): { min: number; max: number } {
  return {
    min: SAFE_CORE_LEFT[aspect] + DIRECTOR_X_EDGE_MARGIN,
    max: 1 - SAFE_CORE_RIGHT[aspect] - DIRECTOR_X_EDGE_MARGIN,
  };
}

export type InfographicAspect = "9:16" | "16:9";

export type InfographicCaptionSize = "sm" | "md" | "lg" | "xl";

/**
 * md 대비 자막 배율 — 프론트 `CAPTION_SIZE_SCALE` 의 몫(`scale[size] / scale.md`)과 **같은 값**이어야 한다.
 *
 * ⚠️ `sm` 만 리터럴이 다르다: 프론트는 `0.072 / 0.09 = 0.7999999999999999` 인데 여기는 `0.8` 이라
 *   **1 ULP 차**다(나머지 셋은 같은 나눗셈식·정수라 리터럴까지 동일). 그럼에도 아래
 *   `ssulcineCaptionSafeTop` 을 통과한 결과는 8종(4크기 × 2화면비) 전부 비트 동일이다 —
 *   그 우연을 잠그는 것이 `tests/unit/issue-1884-caption-size-ef.test.ts` 의 anti-drift 표다.
 *   ★그래서 이 표의 숫자를 손대면 반드시 그 스위트를 같이 돌려라★ — 1 ULP 는 눈으로 안 보인다.
 */
const CAPTION_SIZE_RATIO: Record<InfographicCaptionSize, number> = {
  sm: 0.8,
  md: 1,
  lg: 0.116 / 0.09,
  xl: 0.145 / 0.09,
};

export function isInfographicCaptionSize(value: unknown): value is InfographicCaptionSize {
  return value === "sm" || value === "md" || value === "lg" || value === "xl";
}

/**
 * 썰씨네 번인 자막 블록의 정규화 상단. 프론트 `ssulcineCaptionTopRatio`와 같은 하단 앵커
 * 기하를 독립적으로 계산한다(Edge Function은 src alias를 import하지 않는다).
 */
export function ssulcineCaptionSafeTop(captionSize: InfographicCaptionSize, aspect: InfographicAspect): number {
  const ratio = CAPTION_SIZE_RATIO[captionSize];
  // SSULCINE_CAPTION_SPEC: md bottom=566, lineGap=33, 2 rows, frameH=640.
  const startY = 566 - 66 * ratio;
  // ★연산 순서는 프론트와 **다르다**★ — 프론트 `ssulcineCaptionMetrics` 는 `566 − 2 × (33 × ratio)`,
  //   여기는 `566 − 66 × ratio` 다. "같은 순서로 적어 비트를 맞춘" 것이 아니라, **결과가 동치가 되도록
  //   상수를 골랐고**(66 = 2 × 33) 그 동치를 8종 전수 실측으로 잠갔다
  //   (tests/unit/issue-1884-caption-size-ef.test.ts). 두 식이 항등이라는 보장은 부동소수점에 없다 —
  //   증거는 오직 그 표다. 식·상수를 고치면 표부터 다시 재라.
  const [width, height] = aspect === "16:9" ? [16, 9] : [9, 16];
  const scale = Math.min(width, height) / 360;
  const specTop = height - (640 - startY) * scale;
  // 📐 세이프존 접기(2026-09-23) — 프론트 `foldCaptionBlockToSafe` 와 같은 셈: 블록(2줄 × 줄 간격) 바닥이 core 하단을
  //   넘지 않게 **위로만** 민다. 16:9 는 규격 블록이 이미 안이라 값이 그대로다(8종 표가 잠근다).
  const bottomLimit = height * (1 - SAFE_CORE_BOTTOM[aspect]);
  return Math.min(specTop, bottomLimit - 2 * ((33 * ratio) * scale)) / height;
}
